import React from "react";
import { useDispatch, useSelector } from "react-redux";
import get from "lodash/get";
import PropTypes from "prop-types";

import { collectionToStories } from "@quintype/components";
import { getCategoryAndSubCategory, useTrackWidgetImpression } from "../../../../ga-utils";
import { getTemplateName } from "../../../../components/utils";

import { CollectionName } from "../../Atoms/CollectionName";
import { LoadmoreButton } from "../../Atoms/Loadmore";
import { generateNavigateSlug, getTextColor, navigateTo } from "../../../utils/utils";
import { StateProvider } from "../../SharedContext";
import { ProgressiveHydration } from "../../../hydration-component";
import { LoadMoreTarget } from "../../Atoms/LoadMoreTarget";
import PortraitStoryCard from "../../Molecules/PortraitStoryCard";
import ListingAdSlot from "../../../../components/molecules/ListingAdSlot";
import { useDeviceType } from "../../../../components/customHooks/useDeviceType";

import "./four-col-portrait-stories.m.css";

const loadMore = ({ isLoading, storyItems, getMoreStories, subsequentLoadCount = 4 }) => {
  if (isLoading) return; // prevent multiple clicks/calls
  const limit = subsequentLoadCount;
  const offset = storyItems.length;
  getMoreStories(offset, limit);
};

const FourColPortraitStories = ({
  collection,
  config = {},
  getMoreStories,
  isLoadMoreVisible,
  isLoading,
  isolatedLoadMore,
  pageType,
  isYTPageImage = false,
  isYTPageHeadline= false,
  fetchPriorityHigh = false,
}) => {
  const storyItems = collectionToStories(collection);

  if (!storyItems.length) return null;

  const {
    collectionNameBorderColor = "",
    borderColor = "",
    theme = "",
    border = "",
    collectionNameTemplate = "",
    footerSlotConfig = {},
    footerButton = "",
    customCollectionName,
    navigate = true,
    subsequentLoadCount = 4,
  } = config;

  const { footerSlot } = footerSlotConfig;
  const footerSlotComp = typeof footerSlot === "function" ? footerSlot() : null;
  const textColor = getTextColor(theme);

  const dispatch = useDispatch();
  const qtConfig = useSelector((state) => get(state, ["qt", "config"], {}));
  const isDesktop = useDeviceType() === "desktop";
  const url = generateNavigateSlug(collection, qtConfig);

  const isBrowser = typeof window !== "undefined";
  const isAdfreePage = get(qtConfig, ["subscriber"]) === "true";

  // Use the custom hook for tracking widget impressions
  const componentRef = useTrackWidgetImpression(
    {
      sectionName: collection?.name,
      pageType: pageType,
      location: isBrowser ? window.location : null,
      config: qtConfig,
      threshold: 0.2,
    },
    { disabled: !isBrowser }
  );

  const pushDataLayerEvent = (linkPosition) => {
    if (typeof window !== "undefined" && window.dataLayer) {
      const { category, subCategory, contentId } = getCategoryAndSubCategory(window.location, pageType);
      window.dataLayer.push({
        event: "dl_story_listing_component_click",
        link_position: linkPosition,
        section_name: collection?.name?.toLowerCase(),
        category,
        sub_category: subCategory,
        content_id: contentId,
        template_name: getTemplateName(pageType),
        platform: window.innerWidth >= 768 ? "desktop" : "mobile",
      });
    }
  };

  const handleCollectionNameClick = () => {
    pushDataLayerEvent(-2);
    if (navigate) {
      navigateTo(dispatch, url);
    }
  };

  const handleReadMoreClick = () => {
    pushDataLayerEvent(-1);
    navigateTo(dispatch, url);
  };

  const handleStoryClick = (position) => {
    pushDataLayerEvent(position + 1); // +1 to convert from 0-indexed to 1-indexed
  };

  const getLoadMore = (opts) => {
    if (!isLoadMoreVisible) return null;
    const url = generateNavigateSlug(collection, qtConfig);

    if (opts.isBtnWithArrow) {
      return (
        <div onClick={handleReadMoreClick} style={{ cursor: "pointer" }}>
          <LoadmoreButton
            config={config}
            collection={collection}
            qtConfig={qtConfig}
            isBtnWithArrow={opts?.isBtnWithArrow}
          />
        </div>
      );
    }

    if (isolatedLoadMore) {
      return (
        <LoadMoreTarget
          collection={collection}
          componentName={"FourColPortraitStories"}
          offset={storyItems.length}
          limit={subsequentLoadCount}
        />
      );
    }

    return (
      <ProgressiveHydration>
        <div onClick={() => loadMore(opts)} style={{ cursor: "pointer" }}>
          <LoadmoreButton
            config={config}
            collection={collection}
            template={footerButton}
            qtConfig={qtConfig}
            isBtnWithArrow={opts?.isBtnWithArrow}
          />
        </div>
      </ProgressiveHydration>
    );
  };

  return (
    <div
      className="full-width-with-padding arrow-component"
      data-test-id="four-col-portrait-stories"
      style={{ backgroundColor: theme, color: textColor }}
      ref={componentRef}
    >
      <div styleName="four-col-portrait">
        {pageType !== "section-page" && (
          <div styleName="collection-name-wrapper">
            <div onClick={handleCollectionNameClick} style={{ cursor: "pointer" }}>
              <CollectionName
                collection={collection}
                collectionNameTemplate={collectionNameTemplate}
                collectionNameBorderColor={collectionNameBorderColor}
                customCollectionName={customCollectionName}
                navigate={navigate}
              />
            </div>
            {getLoadMore({ isLoading, storyItems, getMoreStories, subsequentLoadCount, isBtnWithArrow: true })}
          </div>
        )}

        <div styleName="wrapper" className="four-col-portrait-wrapper">
          {(pageType === "section-page" ? storyItems : storyItems.slice(0, 4)).map((story, index) => {
            return (
              <>
                <div onClick={() => handleStoryClick(index)}>
                  <PortraitStoryCard
                    story={story}
                    border={border}
                    theme={theme}
                    config={Object.assign({ showSection: false, showAuthor: false, showReadTime: false }, config)}
                    borderColor={borderColor}
                    key={`story-card-${index}`}
                    fetchPriorityHigh={fetchPriorityHigh}
                    isYTPageImage={isYTPageImage}
                    isYTPageHeadline={isYTPageHeadline}
                  />
                </div>
                {!isAdfreePage && (index+1) % (isDesktop ? 8 : 4) === 0 && (
                  <div key={`separator-${index + story['public-identifier']}`} styleName="full-width-separator">
                    <ListingAdSlot
                      adStyleName="ad-slot-size-728x90-300x250"
                      adType="grid_ad"
                      index={index + story['public-identifier']}
                      pageType="visual-stories"
                    />
                  </div>
                )}
              </>
            );
          })}
        </div>
        {pageType !== "section-page" &&
          getLoadMore({ isLoading, storyItems, getMoreStories, subsequentLoadCount, isBtnWithArrow: false })}
        {footerSlotComp}
      </div>
    </div>
  );
};

FourColPortraitStories.propTypes = {
  /**  collection is the array of objects which is returning by API  */
  collection: PropTypes.object.isRequired,
  /** The config for the Row */
  config: PropTypes.shape({
    borderColor: PropTypes.string,
    theme: PropTypes.string,
    border: PropTypes.bool,
    collectionNameTemplate: PropTypes.string,
    footerButton: PropTypes.string,
    collectionNameBorderColor: PropTypes.string,
    subsequentLoadCount: PropTypes.number,
  }),
  getMoreStories: PropTypes.func,
  isLoadMoreVisible: PropTypes.bool,
  isLoading: PropTypes.bool,
  fetchPriorityHigh: PropTypes.bool,
  isolatedLoadMore: PropTypes.bool,
  pageType: PropTypes.string,
  isYTPageHeadline: PropTypes.bool,
};

FourColPortraitStories.defaultProps = {
  getMoreStories: () => {},
  isLoadMoreVisible: true,
  isLoading: false,
  isYTPageHeadline: false,
};

export default StateProvider(FourColPortraitStories);
